import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';

const GET_STREAMLINE_TOKENS = gql`
  query GetStreamlineTokens {
    streamlineTokens {
      tokenKey
      tokenSecret
      startDate
      endDate
    }
  }
`;

const GET_TOKEN_EXPIRATION = gql`
  query GetTokenExpiration {
    streamlineTokenExpiration {
      expiration
      companyId
      isValid
      daysRemaining
    }
  }
`;

const UPDATE_STREAMLINE_TOKENS = gql`
  mutation UpdateStreamlineTokens($input: StreamlineTokensInput!) {
    updateStreamlineTokens(input: $input) {
      success
      message
      tokenKey
      tokenSecret
      startDate
      endDate
    }
  }
`;

const RENEW_STREAMLINE_TOKENS = gql`
  mutation RenewStreamlineTokens {
    renewStreamlineTokens {
      success
      message
      tokenKey
      tokenSecret
      startDate
      endDate
    }
  }
`;

const CHECK_AND_RENEW_TOKENS = gql`
  mutation CheckAndRenewTokenIfNeeded($daysBeforeExpiration: Float) {
    checkAndRenewTokenIfNeeded(daysBeforeExpiration: $daysBeforeExpiration) {
      success
      message
      tokenKey
      tokenSecret
      startDate
      endDate
    }
  }
`;

function StreamlineApiSection() {
  const [tokenKey, setTokenKey] = useState('');
  const [tokenSecret, setTokenSecret] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showTokens, setShowTokens] = useState(false);

  const { data, loading: tokensLoading, error: tokensError, refetch } = useQuery(GET_STREAMLINE_TOKENS);
  const { 
    data: expirationData, 
    loading: expirationLoading, 
    error: expirationError,
    refetch: refetchExpiration 
  } = useQuery(GET_TOKEN_EXPIRATION);
  
  const [updateTokens, { loading: updateLoading }] = useMutation(UPDATE_STREAMLINE_TOKENS, {
    onCompleted: () => {
      toast.success('Streamline API tokens updated successfully');
      setIsEditing(false);
      refetch();
      refetchExpiration();
    },
    onError: (error) => {
      toast.error(`Failed to update tokens: ${error.message}`);
    }
  });

  const [renewTokens, { loading: renewLoading }] = useMutation(RENEW_STREAMLINE_TOKENS, {
    onCompleted: (data) => {
      toast.success('Streamline API tokens renewed successfully');
      refetch();
      refetchExpiration();
    },
    onError: (error) => {
      toast.error(`Failed to renew tokens: ${error.message}`);
    }
  });

  const [checkAndRenewTokens, { loading: checkRenewLoading }] = useMutation(CHECK_AND_RENEW_TOKENS, {
    onCompleted: (data) => {
      const { success, message } = data.checkAndRenewTokenIfNeeded;
      if (success) {
        toast.success(message);
      } else {
        toast.error(message);
      }
      refetch();
      refetchExpiration();
    },
    onError: (error) => {
      toast.error(`Failed to check/renew tokens: ${error.message}`);
    }
  });

  useEffect(() => {
    if (data?.streamlineTokens) {
      setTokenKey(data.streamlineTokens.tokenKey || '');
      setTokenSecret(data.streamlineTokens.tokenSecret || '');
    }
  }, [data]);

  const handleSaveTokens = async () => {
    try {
      console.log('Saving tokens with input:', {
        input: {
          tokenKey,
          tokenSecret
        }
      });
      
      const result = await updateTokens({
        variables: {
          input: {
            tokenKey,
            tokenSecret
          }
        }
      });
      
      console.log('Update tokens response:', result);
    } catch (error) {
      console.error('Error updating tokens:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleRenewTokens = async () => {
    try {
      await renewTokens();
    } catch (error) {
      console.error('Error renewing tokens:', error);
    }
  };

  const handleCheckAndRenewTokens = async () => {
    try {
      await checkAndRenewTokens({
        variables: {
          daysBeforeExpiration: 14 // Check if expiring within 14 days
        }
      });
    } catch (error) {
      console.error('Error checking/renewing tokens:', error);
    }
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const toggleShowTokens = () => {
    setShowTokens(!showTokens);
  };

  // Format display of token to hide most characters
  const formatTokenDisplay = (token) => {
    if (!token) return '';
    if (showTokens) return token;
    return `${token.substring(0, 6)}...${token.substring(token.length - 4)}`;
  };

  // Helper to display token status
  const getExpirationStatus = () => {
    if (expirationLoading) return { text: 'Checking...', color: 'text-gray-500' };
    if (expirationError) return { text: 'Error checking expiration', color: 'text-red-600' };
    
    const expInfo = expirationData?.streamlineTokenExpiration;
    if (!expInfo) return { text: 'Unknown', color: 'text-gray-500' };
    
    if (!expInfo.isValid) return { text: 'Expired!', color: 'text-red-600' };
    if (expInfo.daysRemaining <= 14) return { text: `Expires in ${expInfo.daysRemaining} days`, color: 'text-yellow-600' };
    return { text: `Valid (${expInfo.daysRemaining} days remaining)`, color: 'text-green-600' };
  };

  const expirationStatus = getExpirationStatus();

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Streamline API Configuration</h2>
      
      {tokensLoading ? (
        <div className="flex items-center justify-center py-4">
          <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-2">Loading tokens...</span>
        </div>
      ) : tokensError ? (
        <div className="text-red-600 mb-4">
          Error loading tokens: {tokensError.message}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900">API Tokens</h3>
              <p className="text-sm text-gray-500">
                Manage your Streamline API tokens that expire every 3 months
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={toggleShowTokens}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
              >
                {showTokens ? 'Hide' : 'Show'} Tokens
              </button>
              {!isEditing ? (
                <button
                  onClick={toggleEdit}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Edit Tokens
                </button>
              ) : (
                <button
                  onClick={handleSaveTokens}
                  disabled={updateLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                >
                  {updateLoading ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>

          {/* Token Expiration Status */}
          <div className="mb-4 flex items-center">
            <span className="text-sm font-medium mr-2">Status:</span>
            <span className={`text-sm font-medium ${expirationStatus.color}`}>
              {expirationStatus.text}
            </span>
            <button
              onClick={(e) => {
                e.preventDefault();
                refetchExpiration();
              }}
              className="ml-2 text-xs text-sky-600 hover:text-sky-800"
              disabled={expirationLoading}
            >
              {expirationLoading ? 'Checking...' : 'Check'}
            </button>
          </div>

          {/* Show expiration date if we have it */}
          {(data?.streamlineTokens?.endDate || expirationData?.streamlineTokenExpiration?.expiration) && (
            <div className="mb-4 text-sm">
              <span className="font-medium">Valid until:</span> {
                new Date(expirationData?.streamlineTokenExpiration?.expiration || data?.streamlineTokens?.endDate).toLocaleDateString()
              }
              {new Date(expirationData?.streamlineTokenExpiration?.expiration || data?.streamlineTokens?.endDate) < new Date() && (
                <span className="ml-2 text-red-600 font-medium">Expired!</span>
              )}
            </div>
          )}

          <div className="space-y-4 mb-6">
            <div>
              <label htmlFor="tokenKey" className="block text-sm font-medium text-gray-700">Token Key</label>
              {isEditing ? (
                <input
                  type="text"
                  id="tokenKey"
                  value={tokenKey}
                  onChange={(e) => setTokenKey(e.target.value)}
                  className="mt-1 focus:ring-sky-500 focus:border-sky-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="Enter your Streamline API token key"
                />
              ) : (
                <div className="mt-1 text-sm text-gray-800 font-mono bg-gray-100 p-2 rounded">
                  {formatTokenDisplay(data?.streamlineTokens?.tokenKey || '')}
                </div>
              )}
            </div>
            
            <div>
              <label htmlFor="tokenSecret" className="block text-sm font-medium text-gray-700">Token Secret</label>
              {isEditing ? (
                <input
                  type="text"
                  id="tokenSecret"
                  value={tokenSecret}
                  onChange={(e) => setTokenSecret(e.target.value)}
                  className="mt-1 focus:ring-sky-500 focus:border-sky-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="Enter your Streamline API token secret"
                />
              ) : (
                <div className="mt-1 text-sm text-gray-800 font-mono bg-gray-100 p-2 rounded">
                  {formatTokenDisplay(data?.streamlineTokens?.tokenSecret || '')}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Token Management</h3>
                <p className="text-sm text-gray-500">
                  Manage your Streamline API tokens
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleCheckAndRenewTokens}
                  disabled={checkRenewLoading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                >
                  {checkRenewLoading ? 'Checking...' : 'Check & Renew If Needed'}
                </button>
                <button
                  onClick={handleRenewTokens}
                  disabled={renewLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                >
                  {renewLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Renewing...
                    </>
                  ) : 'Force Renew Tokens'}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This will use your permanent Streamline API credentials stored on the server to request new temporary tokens.
              The new tokens will be valid for approximately 3 months.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default StreamlineApiSection; 